/**
 * Soft-delete helpers. Every "delete" operation in the app goes through
 * one of these instead of calling `table.delete(id)` directly, so the
 * row stays in Dexie with a `deletedAt` timestamp. Sync then propagates
 * the tombstone to other devices, which in turn soft-delete their local
 * copies. Physical purge is deferred (compaction territory) — the short-
 * term benefit is that `reverseSyncToDexie` can only destroy rows that
 * explicitly carry a `deletedAt` flag, which makes entire classes of
 * "oops the doc looks empty, delete everything" bugs impossible.
 *
 * The cost is that every list query must filter out tombstoned rows.
 * See the `visible*` helpers below.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// Loose types — Dexie's generics around `update()` are notoriously
// restrictive for partial-patch signatures and the row types in this
// project have many optional fields. We cast through `any` in the
// helper body and trust the callers to pass a valid table+id pair.

export async function softDelete(
  table: { update: (id: number, changes: any) => Promise<number> },
  id: number,
): Promise<void> {
  await table.update(id, { deletedAt: new Date() });
}

/**
 * Bulk soft-delete via a Dexie WhereClause. Used for cascading deletes
 * like "wipe all diary entries for project X". Relies on Dexie's
 * `.modify()` which applies the patch inside a transaction and fires
 * the `updating` hook for each row.
 */
export async function softDeleteWhere(
  collection: { modify: (changes: any) => Promise<number> },
): Promise<number> {
  return collection.modify({ deletedAt: new Date() });
}

/** True if the row has been soft-deleted. */
export function isDeleted(row: { deletedAt?: Date | string | null }): boolean {
  return row.deletedAt != null;
}

/** Filter out tombstoned rows. Use in list queries:
 *    const all = await db.projects.toArray();
 *    return visible(all);
 */
export function visible<T extends { deletedAt?: Date | string | null }>(rows: T[]): T[] {
  return rows.filter(r => r.deletedAt == null);
}
